import type {
  DocumentReviewEffectiveRulesResponse,
  DocumentReviewReRequestRequest,
  DocumentReviewRequestsListRequest,
  DocumentReviewRoundResponse,
  DocumentReviewRoundsListRequest,
  DocumentReviewRuleCreateRequest,
  DocumentReviewRuleUpdateRequest,
  DocumentReviewRuleResponse,
  GetListResponseOfDocumentReviewRoundListItemResponse,
} from 'src/api/ProactApiClient';
import {documentReviewApiClient} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

/**
 * A review round is derived state: its progress counts come from its task's assignment
 * states and its own state is resolved from them by DocumentReviewResolutionJob. Round
 * queries therefore depend on the task caches as well as their own tag, so that any task
 * completion — a transition, a bulk status update, a course session, a form submission —
 * refetches the review surfaces without every one of those call sites having to know that
 * the document review module exists.
 */
const reviewRoundTags = [
  'DocumentReviewRequests',
  'TaskAssignments',
  'TaskDefinitions',
] as const;

export const documentReviewApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    getDocumentReviewRulesForFolder: builder.query<
      DocumentReviewEffectiveRulesResponse,
      number
    >({
      queryFn: clientQuery(folderId =>
        documentReviewApiClient.getRulesForFolder(folderId)
      ),
      providesTags: ['DocumentReviewRules'],
    }),
    createDocumentReviewRule: builder.mutation<
      DocumentReviewRuleResponse,
      DocumentReviewRuleCreateRequest
    >({
      queryFn: clientQuery(request =>
        documentReviewApiClient.createRule(request)
      ),
      invalidatesTags: ['DocumentReviewRules'],
    }),
    updateDocumentReviewRule: builder.mutation<
      DocumentReviewRuleResponse,
      DocumentReviewRuleUpdateRequest
    >({
      queryFn: clientQuery(request =>
        documentReviewApiClient.updateRule(request)
      ),
      invalidatesTags: ['DocumentReviewRules'],
    }),
    deleteDocumentReviewRule: builder.mutation<void, number>({
      queryFn: voidClientQuery(id => documentReviewApiClient.deleteRule(id)),
      invalidatesTags: ['DocumentReviewRules'],
    }),
    getDocumentReviewRequests: builder.query<
      DocumentReviewRoundResponse[],
      DocumentReviewRequestsListRequest
    >({
      queryFn: clientQuery(request =>
        documentReviewApiClient.getRequests(request)
      ),
      providesTags: [...reviewRoundTags],
    }),
    getDocumentReviewRoundsList: builder.query<
      GetListResponseOfDocumentReviewRoundListItemResponse,
      DocumentReviewRoundsListRequest
    >({
      queryFn: clientQuery(request =>
        documentReviewApiClient.getRequestsList(request)
      ),
      providesTags: [...reviewRoundTags],
    }),
    reRequestDocumentReview: builder.mutation<
      DocumentReviewRoundResponse,
      DocumentReviewReRequestRequest
    >({
      queryFn: clientQuery(request =>
        documentReviewApiClient.reRequestReview(request)
      ),
      // Re-requesting review changes the document's status/pendingVersion and spawns a
      // new task for the round, so the document and the task caches both need to refetch
      // alongside the rounds list.
      invalidatesTags: [
        'DocumentReviewRequests',
        'Documents',
        'TaskDefinitions',
        'TaskAssignments',
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDocumentReviewRulesForFolderQuery,
  useCreateDocumentReviewRuleMutation,
  useUpdateDocumentReviewRuleMutation,
  useDeleteDocumentReviewRuleMutation,
  useGetDocumentReviewRequestsQuery,
  useGetDocumentReviewRoundsListQuery,
  useReRequestDocumentReviewMutation,
} = documentReviewApi;
